/**
 * Number Field Type - Inline Renderers
 *
 * Read-only and editable in-table cell renderers for number columns.
 */

import { useCallback } from 'react';
import type { ColumnDef, CellContext } from '@tanstack/react-table';
import { Text } from 'components/text';
import { CellInput } from 'features/misc/table';
import { useEditableContextOptional } from '../../OptimizedTableProvider';
import { SharedEditableCell } from './SharedEditableCell';
import { clearEditOnSuccess } from './inlineEditUtils';
import { CellContainer } from '../../tableStyles';
import { toast } from 'react-toastify';
import { t } from '@lingui/macro';
import type { ColumnRenderer, RenderOptions, CustomColumn } from '../../columnModel';
import { getCellStringValue } from '../cellValue';

// ============================================================================
// Read-Only Column Renderer
// ============================================================================

/**
 * Number read-only renderer - displays number in a cell container
 * Uses getValue() to support nested accessors
 */
export const numberReadOnlyRenderer = <T extends { id: number | string }>(
  _col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => ({
  cell: (cellContext: CellContext<T, unknown>) => {
    const value = cellContext.getValue();
    return (
      <CellContainer>
        <Text variant="bodyXs">{value != null ? String(value) : ''}</Text>
      </CellContainer>
    );
  },
  tooltipRender: (cell: CellContext<T, unknown>) => {
    const value = cell.getValue();
    return value != null ? String(value) : '';
  },
});

// ============================================================================
// Editable Column Renderer
// ============================================================================

/**
 * Number editable renderer - unified pattern
 * Uses col.id as the data field, auto-infers update
 * Supports conditional editability via editable: (cell) => boolean
 */
export const numberEditableRenderer = <T extends { id: number | string }>(
  col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => {
  const editableProp = col.editable;

  return {
    cell: (context: CellContext<T, unknown>) => {
      const value = getCellStringValue(context, col.id);
      const isRowEditable =
        typeof editableProp === 'function' ? editableProp(context) : editableProp === true;

      if (!isRowEditable) {
        return (
          <CellContainer>
            <Text variant="bodyXs">{value}</Text>
          </CellContainer>
        );
      }

      return <NumberEditableCell context={context} col={col} value={value} />;
    },
    meta: {
      editable: true,
    },
    tooltipRender: (cell: CellContext<T, unknown>) => {
      const value = cell.getValue();
      return value != null ? String(value) : '';
    },
  };
};

const NumberEditableCell = <T extends { id: number | string }>({
  context,
  col,
  value,
}: {
  context: CellContext<T, unknown>;
  col: CustomColumn<T>;
  value: string;
}) => {
  const editableCtx = useEditableContextOptional();
  const clearEdit = useCallback(
    () => editableCtx?.setEditableState?.({ rowIndex: null, columnId: null }),
    [editableCtx?.setEditableState]
  );

  return (
    <SharedEditableCell
      {...context}
      meta={{ editable: true }}
      tooltipTitle={value}
      readModeEl={<span>{value}</span>}
    >
      <CellInput
        value={value || ''}
        type="number"
        onChange={newValue => {
          if (col.onUpdate) {
            const numValue = newValue === '' ? null : Number(newValue);
            if (
              (numValue === null || numValue === undefined || isNaN(numValue)) &&
              'required' in col
            ) {
              toast.error(t`${col.label || 'This field'} is required and cannot be empty`);
              clearEdit();
              return;
            }
            const result = col.onUpdate(context.row.original, {
              [col.id]: numValue,
            } as Partial<T>);
            clearEditOnSuccess(result, clearEdit);
          } else {
            clearEdit();
          }
        }}
      />
    </SharedEditableCell>
  );
};

// ============================================================================
// Typed Renderer Exports (for registry)
// ============================================================================

/** Read-only renderer with proper typing */
export const numberReadOnly: ColumnRenderer = numberReadOnlyRenderer;

/** Editable renderer with proper typing */
export const numberEditable: ColumnRenderer = numberEditableRenderer;
